import React from 'react';

import { UxContext } from '@client/context/UxContext';
import { usePageStore } from '@client/store/pageStore/context/pageContext';
import i18n from '@client/utils/i18n';
import { CircadianEvent, RecipeSection } from '@ledium/types';
import { SelectLightRecipe } from '@ledium/ui-common';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Grid, MenuItem, Select } from '@mui/material';

interface SectionCircadianEventListProps {
    sectionData: RecipeSection;
    setSectionData: (data: RecipeSection) => void;
}

const SectionCircadianEventList = ({ sectionData, setSectionData }: SectionCircadianEventListProps) => {
    const { handleAlert } = React.useContext(UxContext)!;
    const {
        pageState: { formData }
    } = usePageStore();

    const onUpdateEvent = (eventKey: number, property: keyof CircadianEvent, value: unknown) => {
        const events = [...sectionData.circadianEvents.map((e) => ({ hour: e.hour, minute: e.minute, lightRecipeId: e.lightRecipeId }))];
        (events[eventKey] as Record<string, unknown>)[property] = value;
        setSectionData({ ...sectionData, circadianEvents: events });
    };

    const confirmDeleteEvent = (eventIdx: number) => {
        handleAlert({
            title: i18n('delete_circadian_event_title') as string,
            message: i18n('delete_circadian_event_message') as string,
            okCallback: () => {
                setSectionData({ ...sectionData, circadianEvents: sectionData.circadianEvents.filter((_item, idx) => idx !== eventIdx) });
            }
        });
    };

    return (
        <>
            {sectionData.circadianEvents.map((event, eventKey) => (
                <Grid item xs={12} key={`circadian-event-${eventKey}`}>
                    <Grid container spacing={2}>
                        {formData.readOnly ? null : (
                            <Grid item xs={3} md={1}>
                                <Button
                                    style={{ marginTop: '12px' }}
                                    size="small"
                                    variant="contained"
                                    color="secondary"
                                    // eslint-disable-next-line
                                        onClick={() => confirmDeleteEvent(eventKey)}>
                                    <DeleteIcon />
                                </Button>
                            </Grid>
                        )}
                        <Grid item xs={formData.readOnly ? 12 : 9} md={formData.readOnly ? 4 : 3} sx={{ textAlign: 'center' }}>
                            {formData.readOnly ? (
                                <span>{i18n('event_time', <b>{`0${event.hour}`.slice(-2)}</b>, <b>{`0${event.minute}`.slice(-2)}</b>)}</span>
                            ) : (
                                <>
                                    <Select
                                        labelId="circadian-event-hour"
                                        // eslint-disable-next-line
                                        onChange={e => onUpdateEvent(eventKey, 'hour', e.target.value)}
                                        value={event.hour}
                                        variant="outlined"
                                        name="circadianEventHour"
                                    >
                                        {[...Array(24)].map((e, i) => (
                                            <MenuItem key={`circadian-event-${eventKey}-hour-${i}`} value={i}>
                                                {i < 10 ? '0' : ''}
                                                {i}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    &nbsp;:&nbsp;
                                    <Select
                                        labelId="circadian-event-minute"
                                        // eslint-disable-next-line
                                        onChange={e => onUpdateEvent(eventKey, 'minute', e.target.value)}
                                        value={event.minute}
                                        variant="outlined"
                                        name="circadianEventMinute"
                                    >
                                        {[...Array(12)].map((e, i) => (
                                            <MenuItem key={`circadian-event-${eventKey}-minute-${i * 5}`} value={i * 5}>
                                                {i < 2 ? '0' : ''}
                                                {i * 5}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </>
                            )}
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <SelectLightRecipe
                                selectedLightRecipeId={event.lightRecipeId}
                                // eslint-disable-next-line
                                onSelectLightRecipe={lightRecipe => onUpdateEvent(eventKey, 'lightRecipeId', lightRecipe?.id)}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            ))}
        </>
    );
};

export default SectionCircadianEventList;
